import { type Editor, EditorContent, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'

import {
  ActivityLogIcon,
  FontBoldIcon,
  FontItalicIcon,
  ListBulletIcon,
  MinusIcon,
  StrikethroughIcon,
} from '@radix-ui/react-icons'

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/presentation/components/select'
import { Toggle } from '@/presentation/components/toggle'
import { ToggleGroup, Toolbar } from '@/presentation/components/toolbar'
import clsx from 'clsx'
import { useEffect } from 'react'

interface EditorToolbarProps {
  editor: Editor
}

interface RichTextProps {
  content: string
  placeholder?: string
  className?: string
  onChange: (value: string) => void
}

const FormatType = ({ editor }: EditorToolbarProps) => {
  const value = () => {
    if (editor.isActive('paragraph')) return 'paragraph'
    if (editor.isActive('heading', { level: 1 })) return 'h1'
    if (editor.isActive('heading', { level: 2 })) return 'h2'
    if (editor.isActive('heading', { level: 3 })) return 'h3'
    if (editor.isActive('heading', { level: 4 })) return 'h4'
    if (editor.isActive('heading', { level: 5 })) return 'h5'
    if (editor.isActive('heading', { level: 6 })) return 'h6'
  }

  const onChange = (value: string) => {
    switch (value) {
      case 'paragraph':
        editor.chain().focus().setParagraph().run()
        break
      case 'h1':
        editor.chain().focus().toggleHeading({ level: 1 }).run()
        break
      case 'h2':
        editor.chain().focus().toggleHeading({ level: 2 }).run()
        break
      case 'h3':
        editor.chain().focus().toggleHeading({ level: 3 }).run()
        break
      case 'h4':
        editor.chain().focus().toggleHeading({ level: 4 }).run()
        break
      case 'h5':
        editor.chain().focus().toggleHeading({ level: 5 }).run()
        break
      case 'h6':
        editor.chain().focus().toggleHeading({ level: 6 }).run()
        break
    }
  }

  return (
    <Select onValueChange={onChange} defaultValue={value()} value={value()}>
      <SelectTrigger className="invisible h-8 w-[120px] sm:visible">
        <SelectValue placeholder="Selecione o formato" />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          <SelectItem value="paragraph">Texto</SelectItem>
          <SelectItem value="h1">H1</SelectItem>
          <SelectItem value="h2">H2</SelectItem>
          <SelectItem value="h3">H3</SelectItem>
          <SelectItem value="h4">H4</SelectItem>
          <SelectItem value="h5">H5</SelectItem>
          <SelectItem value="h6">H6</SelectItem>
        </SelectGroup>
      </SelectContent>
    </Select>
  )
}

const EditorToolbar = ({ editor }: EditorToolbarProps) => (
  <Toolbar className="m-0 flex items-center justify-between p-2" aria-label="Formatting options">
    <ToggleGroup className="flex flex-row items-center" type="multiple">
      <Toggle
        size="sm"
        className="mr-1"
        onPressedChange={() => editor.chain().focus().toggleBold().run()}
        disabled={!editor.can().chain().focus().toggleBold().run()}
        pressed={editor.isActive('bold')}
      >
        <FontBoldIcon className="h-4 w-4" />
      </Toggle>

      <Toggle
        size="sm"
        className="mr-1"
        onPressedChange={() => editor.chain().focus().toggleItalic().run()}
        disabled={!editor.can().chain().focus().toggleItalic().run()}
        pressed={editor.isActive('italic')}
        value="italic"
      >
        <FontItalicIcon className="h-4 w-4" />
      </Toggle>

      <Toggle
        size="sm"
        className="mr-1"
        onPressedChange={() => editor.chain().focus().toggleStrike().run()}
        disabled={!editor.can().chain().focus().toggleStrike().run()}
        pressed={editor.isActive('strike')}
      >
        <StrikethroughIcon className="h-4 w-4" />
      </Toggle>

      <Toggle
        size="sm"
        className="mr-1"
        onPressedChange={() => editor.chain().focus().toggleBulletList().run()}
        pressed={editor.isActive('bulletList')}
      >
        <ListBulletIcon className="h-4 w-4" />
      </Toggle>

      <Toggle
        size="sm"
        className="mr-1"
        onPressedChange={() => editor.chain().focus().toggleOrderedList().run()}
        pressed={editor.isActive('orderedList')}
      >
        <ActivityLogIcon className="h-4 w-4" />
      </Toggle>

      <Toggle
        size="sm"
        className="mr-1"
        onPressedChange={() => editor.chain().focus().setHorizontalRule().run()}
      >
        <MinusIcon className="h-4 w-4" />
      </Toggle>

      <FormatType editor={editor} />
    </ToggleGroup>
  </Toolbar>
)

export const RichText = ({ content, placeholder, onChange, className }: RichTextProps) => {
  const editor = useEditor({
    extensions: [StarterKit],
    content: content,
    immediatelyRender: false,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML())
    },
    editorProps: {
      attributes: {
        class: 'focus:outline-none',
      },
    },
  })

  useEffect(() => {
    if (content && editor?.getHTML() !== content) {
      editor?.commands.setContent(content)
    }
  }, [editor, content])

  if (!editor) return <></>

  return (
    <div
      className={clsx(
        'prose w-full max-w-none rounded-lg border border-input bg-background outline-none prose-p:my-1',
        className,
      )}
    >
      <EditorToolbar editor={editor} />
      <div className="editor max-h-96 min-h-36 overflow-auto p-4">
        <EditorContent editor={editor} placeholder={placeholder} />
      </div>
    </div>
  )
}
